import type { GraphQLError } from 'graphql';

import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { preloadClassificationContextForViewContent } from '@confluence/data-classification/entry-points/queries.ts';
import { preloadContent } from '@confluence/content-body/entry-points/preloadContent.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { getAutohighlightsQueryVariables } from '@confluence/contextual-reading-aids/entry-points/getAutohighlightsQueryVariables.ts';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadMacroData } from '@confluence/preload-macros/entry-points/preloadMacroData.ts';
import { preloadMacrosSSR } from '@confluence/fabric-extension-handlers/entry-points/preloadMacrosSSR.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadSpace } from '@confluence/space-base/entry-points/preloadSpace.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { preloadScreen } from '@confluence/screen/entry-points/preloadScreen.js';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import SpaceOverviewSwitcherQueryNode, {
	type SpaceOverviewSwitcherQuery$data,
	type SpaceOverviewSwitcherQuery$variables,
} from './__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { isSmartSpaceOverviewEnabled } from './isSmartSpaceOverviewEnabled.ts';
import { preloadGroupedButtons } from './preloadGroupedButtons.ts';

export const preloadSpaceOverviewQuery = (
	spaceKey: string,
): Promise<{
	data: SpaceOverviewQuery$data | null;
	errors?: ReadonlyArray<GraphQLError>;
	hasErrors: boolean;
}> =>
	query<SpaceOverviewQuery$data, SpaceOverviewQuery$variables>({
		query: SpaceOverviewQuery,
		variables: {
			spaceKey,
			excludeOperations: Boolean(!isCompanyHubSpaceKey(spaceKey)),
		},
	});

export const preloadSpaceOverviewSwitcherQuery = (
	spaceKey: string,
):
	| Promise<null>
	| Promise<{
			data: SpaceOverviewSwitcherQuery$data | null;
			errors?: ReadonlyArray<GraphQLError>;
			hasErrors: boolean;
	  }> => {
	const { cloudId, isRovoEnabled } = getPreloaderFnContext();

	if (
		!isSmartSpaceOverviewEnabled({
			spaceKey,
			cloudId,
			isRovoEnabled,
		})
	) {
		return Promise.resolve(null);
	}

	return query<SpaceOverviewSwitcherQuery$data, SpaceOverviewSwitcherQuery$variables>({
		query: SpaceOverviewSwitcherQueryNode,
		variables: {
			cloudId,
			spaceKey,
		},
	});
};

type PreloadSpaceOverviewQueriesParameters = {
	spaceKey: string;
	homepageId?: string;
} & Pick<Parameters<typeof preloadContent>[0], 'versionOverride'>;

const preloadSpaceOverviewQueries = async ({
	homepageId = '',
	spaceKey,
	versionOverride,
}: PreloadSpaceOverviewQueriesParameters): Promise<any> => {
	const tasks: Promise<any>[] = [];

	const { activationId, userId, cloudId } = getPreloaderFnContext();

	let contentNodesPromiseResolve: ((value: any) => void) | undefined = undefined;
	const contentNodesPromise = new Promise<any>((resolve) => {
		contentNodesPromiseResolve = resolve;
	});

	if (homepageId) {
		// If we know the homepageId, preload early
		tasks.push(preloadClassificationContextForViewContent(homepageId));
		if (fg('conf_frontend_forge_static_macro_mvp')) {
			tasks.push(
				preloadUseExtensionList({
					contentId: homepageId,
					spaceKey,
					moduleType: FORGE_MODULE_XEN_MACRO,
				}),
			);
		}

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			tasks.push(
				preloadMacrosSSR({
					contentId: homepageId,
					cloudId,
					useMultipleMacrosQuery: true,
					contentNodes: contentNodesPromise,
				}),
			);
		}

		tasks.push(
			prepareGuardExceptionTask('MacroData', () =>
				preloadMacroData({
					contentNodes: contentNodesPromise,
					context: { contentId: homepageId, spaceKey },
					route: 'space-overview',
				}),
			),
		);
	}

	const autohighlightsQueryVariables =
		homepageId &&
		userId &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_autohighlights_unified_query') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
			? getAutohighlightsQueryVariables({
					contentId: homepageId,
					cloudId,
					activationId,
					contentType: 'page',
				})
			: undefined;

	tasks.push(
		prepareGuardExceptionTask('ContentAndMacros', () =>
			preloadContent({
				contentId: homepageId,
				spaceKey,
				isBlog: false,
				versionOverride,
				userId,
				// Mirror preloadViewPageRoute so the space-overview homepage preload uses the same
				// ContentUnifiedQuery variable shape (and cache key) as the live ViewPageContainer read.
				// confluence-feature-gating/inline-usage: expValEquals must be inlined at the callsite.
				agentSessionsEnabled: expValEquals('cc_ai_agent_mention_from_comment', 'isEnabled', true),
				...(autohighlightsQueryVariables ? { autohighlightsQueryVariables } : {}),
			}).then(async ({ result, hasErrors }): Promise<any> => {
				const preloadedContentId = result?.data?.content?.nodes?.[0]?.id;
				if (hasErrors || (homepageId && preloadedContentId !== homepageId)) {
					getMonitoringClient().submitError(
						new Error(
							`SpaceOverview preload did not produce the expected ContentUnifiedQuery entry; spaceKey=${spaceKey}; requestedHomepageId=${homepageId}; preloadedContentId=${preloadedContentId}; hasErrors=${Boolean(hasErrors)}`,
						),
						{ attribution: 'next-space-overview' },
					);
				}

				// Report the content query's own errors as SSR prepare errors. `preloadContent` runs
				// with `errorPolicy: 'all'`, so a failing content query resolves rather than rejecting,
				// and this task resolves with its follow-up preloads instead of the query result — so
				// nothing ever inspected `result.errors`. `waitForAllReturnWhenError` skips the
				// expected shapes (restricted page, not found) that `query()` marked as handled.
				await waitForAllReturnWhenError(result);

				const contentId = homepageId || result?.data?.content?.nodes[0]?.id;
				// in the event that there are errors (no id from result) and the homepageId is not valid (error when loading the space),
				// bypass preloadContentPrereqs since contentId is undefined
				if (!contentId) {
					return;
				}

				contentNodesPromiseResolve?.(result?.data?.content?.nodes);

				const promises: Promise<any>[] = [];
				if (!homepageId) {
					// If we don't know the homepageId, preloadContent will tell us the id
					promises.push(
						preloadMacrosSSR({
							contentId,
							cloudId,
							useMultipleMacrosQuery: true,
							contentNodes: result?.data?.content?.nodes,
						}),
					);
					if (fg('conf_frontend_forge_static_macro_mvp')) {
						promises.push(
							preloadUseExtensionList({
								contentId,
								spaceKey,
								moduleType: FORGE_MODULE_XEN_MACRO,
							}),
						);
					}
					tasks.push(preloadClassificationContextForViewContent(contentId));
					promises.push(
						preloadMacroData({
							contentNodes: result?.data?.content?.nodes,
							context: { contentId, spaceKey },
							route: 'space-overview',
						}),
					);
				}
				if (hasErrors) {
					promises.push(preloadContentPrerequisites(contentId, spaceKey, { versionOverride }));
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				} else if (process.env.REACT_SSR) {
					promises.push(
						preloadMacrosSSR({
							contentId,
							cloudId,
							useMultipleMacrosQuery: false,
							contentNodes: result?.data?.content?.nodes,
						}),
					);
				}
				return waitForAllReturnWhenError(promises);
			}),
		),
		preloadGroupedButtons({
			contentId: homepageId,
			spaceKey,
		}),
	);

	tasks.push(preloadPageTitleContentProperties({ contentId: homepageId, spaceKey }));

	// Navigation (including page tree + blog tree) is preloaded by preloadSpaceOverviewRoute via
	// preloadNavigationTasks, so it is intentionally not preloaded here.
	return waitForAllReturnWhenError(tasks);
};

type PreloadSpaceOverviewParameters = {
	spaceKey: string;
	homepageId: string | undefined;
	homepageIdPromise?: Promise<string | undefined>;
} & Pick<PreloadSpaceOverviewQueriesParameters, 'versionOverride'>;

export async function preloadSpaceOverview({
	spaceKey,
	homepageId,
	homepageIdPromise,
	versionOverride,
}: PreloadSpaceOverviewParameters): Promise<any> {
	const promises = [
		preloadScreen(spaceKey) as Promise<any>,
		preloadSpace(spaceKey) as Promise<any>,
		preloadCustomHeaderAndFooter(spaceKey),
		preloadSpaceOverviewSwitcherQuery(spaceKey),
	];

	if (homepageId || homepageIdPromise) {
		const resolvedHomepageId = homepageId ?? (await homepageIdPromise);
		// In some case we append homepageId as a query param. Then preload with it.
		// next/packages/space-overview/src/SpaceOverview.tsx still needs SpaceOverviewQuery but others are not depending on it
		promises.push(
			preloadSpaceOverviewQuery(spaceKey),
			preloadSpaceOverviewQueries({
				homepageId: resolvedHomepageId,
				spaceKey,
				versionOverride,
			}),
		);
		if (expVal('confluence_preload_share_and_restrict_button_query', 'isEnabled', false)) {
			promises.push(preloadShareAndRestrictButtonQuery(resolvedHomepageId, spaceKey));
		}
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	} else if (process.env.REACT_SSR) {
		// Resolve the homepage ID from the SpaceOverviewQuery before preloading content. Preloading
		// by space key transforms a different query into ContentUnifiedQuery, whose cache key can
		// diverge from ViewPageContainer when document fragments are enabled.
		const spaceOverviewQuery = preloadSpaceOverviewQuery(spaceKey);
		promises.push(
			spaceOverviewQuery,
			spaceOverviewQuery.then((res) => {
				const actualHomepageId = res?.data?.space?.homepage?.id;
				if (actualHomepageId) {
					return preloadSpaceOverviewQueries({
						homepageId: actualHomepageId,
						spaceKey,
						versionOverride,
					});
				}
			}),
		);
		if (expVal('confluence_preload_share_and_restrict_button_query', 'isEnabled', false)) {
			promises.push(preloadShareAndRestrictButtonQuery(undefined, spaceKey));
		}
	} else {
		if (expVal('confluence_preload_share_and_restrict_button_query', 'isEnabled', false)) {
			// Preload share button data with spaceKey early (before homepageId is known)
			promises.push(preloadShareAndRestrictButtonQuery(undefined, spaceKey));
		}

		// If we have no information on home id and yet using old query, we need to fetch it first.
		promises.push(
			preloadSpaceOverviewQuery(spaceKey).then((res) => {
				// Because homepageId was provided by the function caller, it may or may
				// not be out of sync with backend:
				const actualHomepageId = res?.data?.space?.homepage?.id;
				if (actualHomepageId) {
					return preloadSpaceOverviewQueries({
						homepageId: actualHomepageId,
						spaceKey,
						versionOverride,
					});
				}
			}),
		);
	}

	return waitForAllReturnWhenError(promises);
}
